import {Component, inject, OnInit, signal} from '@angular/core';
import {RouterOutlet} from '@angular/router';
import {Sidebar} from './shared/sidebar/sidebar';
import {Header} from './shared/header/header';
import {Loading} from './shared/loading/loading';
import {GeographyService} from './core/services/geography-service';
import {AppApi} from './core/services/app-api';
import {SubscriptionPlanService} from './core/services/subscription-plan-service';
import {ISubscriptionPlanResponse} from './shared/models/i-subscription-plan-response';
import {ColorService} from './core/services/color-service';
import {MessageList} from './shared/message-list/message-list';
import {MessageService} from './shared/services/message-service';
import {SiteUnderMaintenance} from './shared/site-under-maintenance/site-under-maintenance';
import {environment} from '../environments/environment.local';
import {FormStateService} from './shared/services/form-state-service';
import {ScrollService} from './core/services/scroll-service';
import {UpgradeDialogService} from './shared/services/upgrade-dialog-service';
import {StripeApi} from './shared/services/stripe-api';
import {ProfileSync} from './auth/services/profile-sync';
import {Billing} from './features/profile/billing/billing';
import {NotificationService} from './shared/services/notification-service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Sidebar, Header, Loading, MessageList, SiteUnderMaintenance, Billing],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  messageService: MessageService = inject(MessageService);
  geographyService: GeographyService = inject(GeographyService);
  appApi: AppApi = inject(AppApi);
  subscriptionPlanService: SubscriptionPlanService = inject(SubscriptionPlanService);
  colorService: ColorService = inject(ColorService);
  formStateService: FormStateService = inject(FormStateService);
  scrollService: ScrollService = inject(ScrollService);
  upgradeDialogService: UpgradeDialogService = inject(UpgradeDialogService);
  stripeApi: StripeApi = inject(StripeApi);
  profileSync: ProfileSync = inject(ProfileSync);
  notificationService: NotificationService = inject(NotificationService);

  protected readonly title = signal('blinth');

  isMaintenance: boolean = false;

  loading: boolean = false;
  loadedApp: boolean = false;

  backendReady: boolean = false;
  isChecking: boolean = false;

  plans: ISubscriptionPlanResponse[] = [];
  selectedPlanToPurchase: ISubscriptionPlanResponse | null = null;
  hasTaxInformation = false;

  ngOnInit() {
    if (this.isMaintenance) return;

    this.hasTaxInformation = !!this.profileSync.user()?.user?.taxInformation;

    this.initApp();
  }

  initApp(): void {
    if (environment.app) {
      this.loadedApp = true;
      return this.healthCheck();
    }

    this.getGlobalData();
  }

  getGlobalData(): void {
    this.appApi.getGlobalData()
      .subscribe({
        next: data => {
          this.loading = false;
          this.geographyService.setProvinces(data.provinces);
          this.notificationService.setBadges(data.notificationBadges);

          if (!environment.app) {
            this.profileSync.updatePlan(data.myPlan);

            this.subscriptionPlanService.setPlans(data.subscriptionPlans);
            this.plans = data.subscriptionPlans;

            const proPlan: ISubscriptionPlanResponse | undefined = data.subscriptionPlans
              .find(p => p.name.toLowerCase() === 'pro');

            if (!proPlan) return;
            this.subscriptionPlanService.setCheckoutPlan(proPlan);
          }

          this.loadedApp = true;
          this.backendReady = true;
        },
        error: err => {
          this.loading = false;
          this.messageService.handleError(err);
        }
      })
  }

  healthCheck(): void {
    if (this.isChecking || this.backendReady) return;

    this.isChecking = true;

    this.appApi.healthCheck()
      .subscribe({
        next: () => {
          this.isChecking = false;
          this.getGlobalData();
        },
        error: () => {
          this.isChecking = false;
          setTimeout(() => {
            this.healthCheck();
          }, 2000)
        }
      })
  }

  handleUpgradeClick(): void {
    this.upgradeDialogService.close();
    this.hasTaxInformation = !!this.profileSync.user()?.user?.taxInformation;
    this.upgradePlan();
  }

  onBillingSaved(): void {
    this.hasTaxInformation = true;
    if (this.selectedPlanToPurchase) {
      this.proceedToUpgradePortal();
    }
  }

  upgradePlan(): void {
    const plan = this.plans.find(p => p.name.toLowerCase() === 'pro');
    if (!plan) return;

    this.selectedPlanToPurchase = plan;

    if (!this.hasTaxInformation) {
      return;
    }

    this.proceedToUpgradePortal();
  }

  private proceedToUpgradePortal(): void {
    this.loading = true;

    this.stripeApi.createUpgradeSession().subscribe({
      next: (response) => {
        this.loading = false;
        window.location.href = response.url;
      },
      error: (err) => {
        this.loading = false;
        this.messageService.handleError(err);
      }
    });
  }

  protected readonly environment = environment;
}
